import React from "react";
import { Box, Typography, Grid } from "@mui/material";
import AvatarStack from "../../../profile/AvatarStack";
import { PlayerScore } from "../types/score";

interface WinnerLoserProps {
  scores: PlayerScore[];
  viewMode: "week" | "year";
  userEmail?: string;
}

const WinnerLoser: React.FC<WinnerLoserProps> = ({
  scores,
  viewMode,
  userEmail,
}) => {
  // Check if any players have wins or losses (using backend fields)
  const hasWinnersOrLosers = scores.some(
    (player) => (player.wins || 0) > 0 || (player.losses || 0) > 0
  );

  if (!hasWinnersOrLosers) {
    return null;
  }

  const isSeason = viewMode === "year";

  // Get winners - rank 1 for year view, or players with wins for week view
  const winners = scores.filter((player) => {
    if (isSeason) {
      return player.rank === 1;
    } else {
      return (player.wins || 0) > 0;
    }
  });

  // Get losers - players with losses for week view, or lowest rank for year view
  const losers = scores.filter((player) => {
    if (isSeason) {
      const maxRank = Math.max(...scores.map((player) => player.rank || 0));
      return player.rank === maxRank;
    } else {
      return (player.losses || 0) > 0;
    }
  });

  const winnerLabel =
    winners.length === 1
      ? isSeason
        ? "Current Season Winner"
        : "Winner"
      : isSeason
      ? "Current Season Winners"
      : "Winners";

  const loserLabel =
    losers.length === 1
      ? isSeason
        ? "Current Season Loser"
        : "Loser"
      : isSeason
      ? "Current Season Losers"
      : "Losers";

  const noWinnersText = isSeason ? "No season winners" : "No winners this week";
  const noLosersText = isSeason ? "No season losers" : "No losers this week";

  return (
    <Box sx={{ p: 3, borderBottom: "1px solid #e0e0e0" }}>
      <Grid container spacing={3}>
        {/* Winners */}
        <Grid item xs={12} md={6}>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: "#f59e0b",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              👑 {winnerLabel}
            </Typography>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {winners.length > 0 ? (
                <AvatarStack
                  currentUserEmail={userEmail}
                  userEmails={winners.map((player) => player.email)}
                  max={6}
                  showTooltip={true}
                  useCache={true}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "#9ca3af", fontStyle: "italic" }}
                >
                  {noWinnersText}
                </Typography>
              )}
            </Box>
            {viewMode === "week" && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: "#f59e0b",
                  fontWeight: 600,
                }}
              >
                Score: {winners.length > 0 ? winners[0].points : 0}
              </Typography>
            )}
          </Box>
        </Grid>

        {/* Losers */}
        <Grid item xs={12} md={6}>
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: "#dc2626",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
              }}
            >
              🤡 {loserLabel}
            </Typography>
            <Box
              sx={{
                mb: 2,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {losers.length > 0 ? (
                <AvatarStack
                  currentUserEmail={userEmail}
                  userEmails={losers.map((player) => player.email)}
                  max={6}
                  showTooltip={true}
                  useCache={true}
                />
              ) : (
                <Typography
                  variant="body2"
                  sx={{ color: "#9ca3af", fontStyle: "italic" }}
                >
                  {noLosersText}
                </Typography>
              )}
            </Box>
            {viewMode === "week" && (
              <Typography
                variant="body2"
                sx={{
                  mt: 1,
                  color: "#dc2626",
                  fontWeight: 600,
                }}
              >
                Score: {losers.length > 0 ? losers[0].points : 0}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WinnerLoser;
